import { apiClient } from '@/shared/api/client';

// Fish Health Check response matching backend FishHealthResponse
export interface FishHealthCheck {
  id: string;
  pondId: string;
  farmId: string;
  feedType: string;
  averageWeight: number;
  sampleSize: number;
  tasteTestResult?: string;
  parasiteCode?: string;
  parasiteName?: string;
  mortalityCount?: number;
  mortalityRate?: number;
  growthRate?: number;
  feedConversionRatio?: number;
  behaviorObservations?: string;
  physicalSymptoms?: string;
  treatmentApplied?: string;
  veterinarianNotes?: string;
  observations?: string;
  photosPaths?: string[];
  imageIds?: string[];
  hasPendingImages?: boolean;
  checkedAt: string;
  createdBy?: string;
  createdByName?: string;
  checkerName?: string;
  performedByName?: string | null;
  weatherConditions?: string;
  waterTemperature?: number;
  latitude?: number | null;
  longitude?: number | null;
  createdAt: string;
  updatedAt: string;
  healthScore?: number;
  riskLevel?: 'low' | 'medium' | 'high' | 'critical';
  recommendedActions?: string[];
  // Weather data when available (enriched by backend)
  weatherDataId?: string;
  weatherData?: {
    id: string;
    farmId: string;
    recordedAt: string;
    temperatureCelsius?: number;
    humidityPercent?: number;
    precipitationMm?: number;
    windSpeedKmh?: number;
    windDirectionDegrees?: number;
    pressureHpa?: number;
    cloudCoverPercent?: number;
    uvIndex?: number;
    weatherCode?: number;
    weatherDescription?: string;
    source: string;
    sourceModel?: string;
    fetchedAt: string;
    createdAt: string;
  };
  // Pond info when included
  pond?: {
    id: string;
    name: string;
    fishType: string;
    waterType: string;
  };
}

export interface FishHealthStatistics {
  averageMortalityRate: number;
  averageWeight: number;
  averageGrowthRate?: number;
  totalChecks: number;
  checksThisWeek?: number;
  checksThisMonth?: number;
  parasiteIncidenceRate: number;
  trendsOverTime: {
    mortalityRate: Array<{ date: string; value: number }>;
    averageWeight: Array<{ date: string; value: number }>;
  };
}

export interface TrendDataPoint {
  date: string;
  value: number;
}

export interface FishHealthCreateRequest {
  feedType: string
  averageWeight: number
  sampleSize: number
  checkedAt: string
  mortalityCount?: number
  parasiteCode?: string
  parasiteName?: string
  behaviorObservations?: string
  physicalSymptoms?: string
  treatmentApplied?: string
  veterinarianNotes?: string
  imageIds?: string[]
}

export type FishHealthUpdateRequest = Partial<FishHealthCreateRequest>

export const fishHealthApi = {
  // Create a new fish health check
  create: async (
    farmId: string,
    pondId: string,
    data: FishHealthCreateRequest
  ): Promise<FishHealthCheck> => {
    const response = await apiClient.post<{ data: FishHealthCheck }>(
      `/farms/${farmId}/ponds/${pondId}/fish-health`,
      data
    )
    return response.data.data
  },

  // Update an existing fish health check
  update: async (
    farmId: string,
    pondId: string,
    checkId: string,
    data: FishHealthUpdateRequest
  ): Promise<FishHealthCheck> => {
    const response = await apiClient.put<{ data: FishHealthCheck }>(
      `/farms/${farmId}/ponds/${pondId}/fish-health/${checkId}`,
      data
    )
    return response.data.data
  },

  // List checks for a farm
  listChecks: async (
    farmId: string,
    params?: {
      cursor?: string;
      limit?: number;
      pondId?: string;
      startDate?: string;
      endDate?: string;
      filter?: 'health_issues' | 'parasites' | 'treatment';
    }
  ) => {
    const response = await apiClient.get<{
      data: {
        checks: FishHealthCheck[];
        pagination: { nextCursor?: string; hasNext: boolean };
      };
    }>(`/farms/${farmId}/fish-health`, { params });
    return { data: response.data.data.checks, pagination: response.data.data.pagination };
  },

  // Get checks for a specific pond
  getPondChecks: async (
    farmId: string,
    pondId: string,
    params?: { cursor?: string; limit?: number }
  ) => {
    const response = await apiClient.get<{
      data: FishHealthCheck[];
      pagination: { nextCursor?: string; hasNext: boolean };
    }>(`/farms/${farmId}/ponds/${pondId}/fish-health`, { params });
    return response.data;
  },

  // Get a specific check
  getCheck: async (farmId: string, pondId: string, checkId: string) => {
    const response = await apiClient.get<{ data: FishHealthCheck }>(
      `/farms/${farmId}/ponds/${pondId}/fish-health/${checkId}`
    );
    return response.data.data;
  },

  // Get statistics for a farm
  getStatistics: async (
    farmId: string,
    params?: { pondIds?: string; startDate?: string; endDate?: string }
  ) => {
    const response = await apiClient.get<{ data: FishHealthStatistics }>(
      `/farms/${farmId}/fish-health/stats`,
      { params }
    );
    return response.data.data;
  },

  // Get trend data for a specific parameter
  getTrends: async (
    farmId: string,
    params: {
      parameter: 'mortality' | 'growth_rate' | 'fcr';
      days: number;
      pondId?: string;
    }
  ) => {
    const response = await apiClient.get<{ data: TrendDataPoint[] }>(
      `/farms/${farmId}/fish-health/trends`,
      { params }
    );
    return response.data;
  },

  // Get parasite statistics for a farm
  getParasiteHistory: async (
    farmId: string,
    params?: { days?: number; pondIds?: string }
  ) => {
    const response = await apiClient.get<{
      data: {
        parasites: Record<string, number>;
        totalIncidents: number;
        affectedPonds: number;
      };
    }>(`/farms/${farmId}/parasites/stats`, { params });
    return response.data.data;
  },

  // Export checks as CSV
  exportChecks: async (
    farmId: string,
    params?: { pondId?: string; startDate?: string; endDate?: string }
  ) => {
    const response = await apiClient.get(`/farms/${farmId}/fish-health/export`, {
      params,
      responseType: 'blob',
    });
    return response.data;
  },
};
